import { instanceBo } from "../utils/axios.create";
import {
	TreatmentPlan,
	TreatmentPlanRequest,
} from "../domain/treatmentPlan.interface";

const getTreatmentPlans = async (
	treatmentPlanId?: string,
): Promise<TreatmentPlan[]> => {
	const pathTreatmentPlanId = treatmentPlanId ? `/${treatmentPlanId}` : "";

	return instanceBo
		.get(`/treatmentplans${pathTreatmentPlanId}`)
		.then((res) => {
			// API returns single object when searching by ID, array otherwise
			const data = res.data.body;
			return Array.isArray(data) ? data : [data];
		})
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const getTreatmentPlan = async (
	treatmentPlanId: string,
): Promise<TreatmentPlan> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const createTreatmentPlan = async (
	treatmentPlan: TreatmentPlanRequest,
): Promise<TreatmentPlan> => {
	return instanceBo
		.post("/treatmentplans", treatmentPlan)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const updateTreatmentPlan = async (
	treatmentPlanId: string,
	treatmentPlan: TreatmentPlanRequest,
): Promise<TreatmentPlan> => {
	return instanceBo
		.put(`/treatmentplans/${treatmentPlanId}`, treatmentPlan)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const deleteTreatmentPlan = async (treatmentPlanId: string): Promise<void> => {
	return instanceBo
		.delete(`/treatmentplans/${treatmentPlanId}`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export interface GenerateSmartProgramPayload {
	description: string;
	preferredMode?: string;
}

export interface GenerateSmartProgramResponse {
	workflow: Record<string, unknown>;
	fieldDefinitions: Array<{
		fieldName: string;
		fieldLabel: string;
		fieldType: string;
		required: boolean;
		options?: string[];
		helpText?: string;
	}>;
	previewHtml: string;
	description: string;
	prompt: string;
}

export interface GenerationStatusResponse {
	jobId: string | null;
	status: "PENDING" | "IN_PROGRESS" | "SUCCESS" | "FAILED" | null;
	error: string | null;
	result: GenerateSmartProgramResponse | null;
}

const generateSmartProgram = async (
	treatmentPlanId: string,
	payload: GenerateSmartProgramPayload,
): Promise<{ jobId: string }> => {
	return instanceBo
		.post(`/treatmentplans/${treatmentPlanId}/generate-smart-program`, payload)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const getGenerationStatus = async (
	treatmentPlanId: string,
): Promise<GenerationStatusResponse> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}/generate-smart-program/status`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export interface RequiredFieldsResponse {
	programType: string;
	fieldDefinitions: Array<{
		fieldName: string;
		fieldLabel: string;
		fieldType: string;
		required: boolean;
		options?: string[];
		defaultValue?: string;
		helpText?: string;
	}>;
}

const getRequiredFields = async (
	treatmentPlanId: string,
): Promise<RequiredFieldsResponse> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}/required-fields`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export const treatmentPlanService = {
	getTreatmentPlans,
	getTreatmentPlan,
	createTreatmentPlan,
	updateTreatmentPlan,
	deleteTreatmentPlan,
	generateSmartProgram,
	getGenerationStatus,
	getRequiredFields,
};
